import React from 'react';
import { Input, TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { MAX_LIST_SIZE } from '../constants';
import {
    DeliveryTime,
    DeliveryTimeOtherEntry,
    countDeliveryTimeRanges,
} from '../../aoi-platform/utils/deliveryTime';

// TimePicker 回填要用 dayjs('09:00', 'HH:mm') 解析, 必须挂 customParseFormat 插件
dayjs.extend(customParseFormat);

interface Props {
    value: DeliveryTime;
    onChange: (next: DeliveryTime) => void;
    readonly?: boolean;
}

const TIME_FORMAT = 'HH:mm';

/**
 * 开门时间编辑器 —— 每行一条「名称 + 时段」。
 *
 * <p><b>为什么不再分工作日 / 休息日 / 节假日三块</b>: 产品 demo 是自由文本行 (如"周一至周五：10:00 - 16:00"),
 * 固定三分类反而限制表达 (半天班、隔周、活动日都塞不进去)。本期统一按"自定义条目"处理:
 * 名称自己写, 时段用时间选择器保证格式规范 (纯文本会写出各种花样, 后端没法用)。
 *
 * <p><b>存储 schema 一点没动</b>: 全部写进已定稿的 `other` 列表 (`{name, ranges, remark}`),
 * workday / restday / holiday 保持原样透传 —— 后端 delivery_time 的 JSON 结构、校验逻辑,
 * 以及标准化地址平台那边的读取都不用改; 历史数据里已有的三分类内容也不会被这里清掉。
 */
const OpenHoursEditor: React.FC<Props> = ({ value, onChange, readonly = false }) => {
    const rows = value.other;
    const total = countDeliveryTimeRanges(value);

    const setRows = (next: DeliveryTimeOtherEntry[]) => onChange({ ...value, other: next });

    const update = (idx: number, patch: Partial<DeliveryTimeOtherEntry>) => {
        setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const remove = (idx: number) => setRows(rows.filter((_, i) => i !== idx));

    const add = () => setRows([...rows, { name: '', ranges: [{ start: '09:00', end: '18:00' }], remark: null }]);

    /** 一行只编辑一个时段 (demo 就是一行一个); 历史数据里多出来的时段原样保留, 不丢。 */
    const rangeOf = (r: DeliveryTimeOtherEntry) => r.ranges[0] ?? { start: '', end: '' };

    const setRange = (idx: number, start: string, end: string) => {
        const rest = rows[idx].ranges.slice(1);
        update(idx, { ranges: [{ start, end }, ...rest] });
    };

    const toDayjs = (hhmm: string): Dayjs | null => {
        if (!hhmm) return null;
        const d = dayjs(hhmm, TIME_FORMAT);
        return d.isValid() ? d : null;
    };

    return (
        <div>
            <label className="mb-2 block text-sm" style={{ color: 'rgba(0,0,0,.88)' }}>
                开门时间
                <span style={{ color: '#a5adb9', fontWeight: 500 }}>（可多行）</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rows.map((r, i) => {
                    const range = rangeOf(r);
                    return (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                padding: 8,
                                border: '1px solid #e5e9f0',
                                borderRadius: 8,
                                background: '#f7f9fc',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Input
                                    size="small"
                                    style={{ flex: 1 }}
                                    placeholder="如 周一至周五 / 节假日"
                                    value={r.name}
                                    disabled={readonly}
                                    maxLength={40}
                                    onChange={(e) => update(i, { name: e.target.value })}
                                />
                                {!readonly && (
                                    <button
                                        type="button"
                                        title="删除"
                                        onClick={() => remove(i)}
                                        style={{
                                            flex: 'none',
                                            width: 24,
                                            height: 24,
                                            border: 'none',
                                            borderRadius: 6,
                                            background: 'transparent',
                                            color: '#a5adb9',
                                            fontSize: 13,
                                            cursor: 'pointer',
                                        }}
                                    >✕</button>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <TimePicker
                                    size="small"
                                    format={TIME_FORMAT}
                                    minuteStep={5}
                                    allowClear={false}
                                    placeholder="开始"
                                    style={{ flex: 1 }}
                                    disabled={readonly}
                                    value={toDayjs(range.start)}
                                    onChange={(d) => setRange(i, d ? d.format(TIME_FORMAT) : '', range.end)}
                                />
                                <span style={{ color: '#a5adb9' }}>~</span>
                                <TimePicker
                                    size="small"
                                    format={TIME_FORMAT}
                                    minuteStep={5}
                                    allowClear={false}
                                    placeholder="结束"
                                    style={{ flex: 1 }}
                                    disabled={readonly}
                                    value={toDayjs(range.end)}
                                    onChange={(d) => setRange(i, range.start, d ? d.format(TIME_FORMAT) : '')}
                                />
                            </div>
                        </div>
                    );
                })}
                {readonly && rows.length === 0 && (
                    <div className="text-xs text-gray-400">暂无开门时间</div>
                )}
                {!readonly && (
                    <button
                        type="button"
                        onClick={add}
                        disabled={total >= MAX_LIST_SIZE}
                        style={{
                            height: 34,
                            border: '1px dashed #cdd6e2',
                            borderRadius: 8,
                            background: '#fff',
                            color: 'rgba(0,0,0,.65)',
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: total >= MAX_LIST_SIZE ? 'not-allowed' : 'pointer',
                        }}
                    >＋ 添加时间段</button>
                )}
            </div>
        </div>
    );
};

export default OpenHoursEditor;
